/**
 * useTimeRangeValue — adapts the global TimeFilterContext to the shared
 * TimeRangePicker's value shape ({ startDate, endDate, years, preset }).
 *
 * Extracted from TechComponentsSankeyChart when the picker moved into the
 * app shell (invariant 7: the time filter lives in TopNav and persists
 * across navigation). Any view that needs its own picker for a special
 * case can reuse this instead of re-deriving the mapping.
 */

import { useMemo } from 'react';
import { calculateDateRange } from '@techstack/ui';
import { useTimeFilterContext } from '../contexts/TimeFilterContext';

const PRESET_TO_YEARS = {
  '1m': 1, '3m': 1, '6m': 1, '1y': 1, '2y': 2, '3y': 3, '5y': 5, all: null,
};

export function useTimeRangeValue() {
  const { timePeriod, setTimePeriod, dateRange } = useTimeFilterContext();

  const timeRangeValue = useMemo(() => {
    // For custom ranges, use the actual dateRange from context
    if (timePeriod === 'custom' && dateRange?.startDate && dateRange?.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      const years = diffDays > 0 ? Math.ceil(diffDays / 365) || 1 : 1;
      return {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        years,
        preset: 'custom',
      };
    }

    // For presets, calculate from the preset value
    const range = calculateDateRange(timePeriod);
    return {
      startDate: range?.startDate || null,
      endDate: range?.endDate || null,
      years: PRESET_TO_YEARS[timePeriod] ?? null,
      preset: timePeriod || '3y',
    };
  }, [timePeriod, dateRange]);

  return { timeRangeValue, setTimePeriod };
}

export default useTimeRangeValue;
